import { useEffect, useState, useRef } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Button,
  Textarea,
  Badge
} from "./UI";
import { Building2, Upload, Check, Users, Info } from "lucide-react";
import { SettingsAPI } from "api/settings/service";
import { useNavigate } from "react-router-dom";
import { useAlert } from "components/UI_components/Alert";

const mapCompanyData = (data) => {
  return {
    name: data.organization_name,
    address: data.business_address,
    gst: data?.gst,
    registration: data?.registration,
    logo: data?.logo_image_file,
  };
};

export function CompanySection() {
  const [company, setCompany] = useState({});
  const { addAlert } = useAlert();
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrganizationData = async () => {
      const orgId = JSON.parse(localStorage.getItem("organization"))?.id;
      if (!orgId) return;
      const res = await SettingsAPI.getOrganization(orgId);
      setCompany(mapCompanyData(res.data));
    };
    fetchOrganizationData();
  }, []);

  const handleUpdate = async () => {
    const orgId = JSON.parse(localStorage.getItem("organization"))?.id;
    if (!orgId) return;
    const payload = {
      organization_name: company.name,
      business_address: company.address,
      // logo_image_file: company.logo,
    }
    await SettingsAPI.updateOrganization(orgId, payload);
    addAlert("Company settings updated", "success");
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const orgId = JSON.parse(localStorage.getItem("organization"))?.id;
    if (!orgId) return;

    const formData = new FormData();
    formData.append("logo_image_file", file);

    try {
      setIsUploading(true);
      const res = await SettingsAPI.updateOrganization(orgId, formData);
      setCompany(prev => ({ ...prev, logo: res.data.logo_image_file }));
      addAlert("Your company logo has been updated successfully.", "success");
    } catch (error) {
      addAlert("Failed to upload logo. Please try again.", "destructive");
    } finally {
      setIsUploading(false);
    }
  };

  const handleLogoClick = () => {
    fileInputRef.current?.click();
  };

  const handleSave = () => {
    handleUpdate();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-primary-text">Company</h2>
          <p className="text-text-gray mt-1">Manage your organization's details.</p>
        </div>
        <Badge variant="primary" className="bg-sm-primary/10 text-sm-primary border-0">
          <Building2 className="h-3 w-3 mr-1" />
          Owner / Admin Only
        </Badge>
      </div>

      <Card className="border-sm-border/50 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Company Logo</CardTitle>
          <CardDescription>Your logo appears on invoices and reports.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            {
              company?.logo ? (
                <img src={company?.logo} alt="Logo" className="w-24 h-24 rounded-xl border border-sm-border/50" />
              ) : (
                <div className="w-24 h-24 rounded-xl bg-gradient-to-br from-sm-primary to-sm-secondary flex items-center justify-center shadow-inner">
                  <span className="text-white font-bold text-2xl">SM</span>
                </div>
              )
            }
            <div className="space-y-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleLogoChange}
                accept="image/*"
                className="hidden"
              />
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={handleLogoClick}
                disabled={isUploading}
              >
                <Upload className="h-4 w-4" />
                {isUploading ? "Uploading..." : company?.logo ? "Change Logo" : "Upload Logo"}
              </Button>
              <p className="text-xs text-text-gray">PNG or SVG. Recommended: 200x200px.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-sm-border/50 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Business Information</CardTitle>
          <CardDescription>Official details for invoices and compliance.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="companyName">Company Name</Label>
            <Input
              id="companyName"
              value={company.name}
              onChange={(e) => setCompany({ ...company, name: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">Registered Address</Label>
            <Textarea
              id="address"
              value={company.address}
              onChange={(e) => setCompany({ ...company, address: e.target.value })}
              className="resize-none"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="gst">GST Number</Label>
              <Input
                id="gst"
                value={company.gst}
                onChange={(e) => setCompany({ ...company, gst: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="registration">Registration / CIN</Label>
              <Input
                id="registration"
                value={company.registration}
                onChange={(e) => setCompany({ ...company, registration: e.target.value })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-sm-border/50 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="h-5 w-5 text-sm-primary" />
            Team & Roles
          </CardTitle>
          <CardDescription>Manage who can access what.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-3 p-4 rounded-lg bg-sm-primary/5 border border-sm-primary/10">
            <Info className="h-5 w-5 text-sm-primary flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-primary-text">Team management is in Organization settings</p>
              <p className="text-sm text-text-gray mt-1">
                Invite team members, assign roles, and manage permissions from the Organization Team section in the sidebar.
              </p>
              <Button variant="link" className="px-0 h-auto text-sm-primary mt-2" onClick={() => navigate("/dashboard/organization-team")}>
                Go to Organization Team →
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} className="gap-2">
          <Check className="h-4 w-4" />
          Save Changes
        </Button>
      </div>
    </div>
  );
}
